import message from "@/utils/globalMessage";
import React, { useRef, useEffect, useState } from 'react';
import { Button, Upload } from "antd";
import { PaperClipOutlined } from '@ant-design/icons';
import './MessageEditor.css';
import ImagePreviewModal from '@components/ImagePreviewModal/ImagePreviewModal';
import { api } from '@redux/api';
import { useSelector } from 'react-redux';

const MessageEditor = ({ onSend, placeholder = "", disabled = false, workorderId, equipmentId }) => {
    const editorRef = useRef(null);
    const [content, setContent] = useState('');
    const [isEmpty, setIsEmpty] = useState(true);
    const [previewImage, setPreviewImage] = useState('');
    const [previewVisible, setPreviewVisible] = useState(false);
    const [uploading, setUploading] = useState(false);
    
    const facilityId = useSelector(state => state.Facility.selected.Id);
    
    // 附件和图片标识符
    const ATTACHMENT_PREFIX = '[ATTACHMENT]';
    const IMAGE_PREFIX = '[IMG]';

    // 解析HTML内容，转换为纯文本+图片+附件的格式
    const parseContentToArray = (htmlContent) => {
        if (!htmlContent) return [];
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        
        const result = [];
        const walker = document.createTreeWalker(
            tempDiv,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            null,
            false
        );
        
        let node;
        while (node = walker.nextNode()) {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent.trim();
                if (text) {
                    result.push(text);
                }
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                // 优先处理包装div中的图片，避免重复处理
                if (node.tagName === 'DIV' && node.classList.contains('message-image-wrapper')) {
                    const img = node.querySelector('img');
                    if (img && img.src && img.src.startsWith('data:image/')) {
                        result.push(`${IMAGE_PREFIX}${img.src}`);
                    }
                } else if (node.tagName === 'IMG') {
                    // 只处理不在包装div中的图片
                    const src = node.src;
                    if (src && src.startsWith('data:image/') && !node.closest('.message-image-wrapper')) {
                        result.push(`${IMAGE_PREFIX}${src}`);
                    }
                }
            }
        }
        
        return result;
    };

    // 更新内容和空状态检查
    const updateContent = () => {
        if (editorRef.current) {
            const textContent = editorRef.current.textContent || editorRef.current.innerText;
            
            // 检查是否有图片
            const hasImages = editorRef.current.querySelectorAll('img').length > 0;
            
            // 如果有文本内容或者有图片，就不为空
            const hasContent = textContent.trim() || hasImages;
            
            setContent(editorRef.current.innerHTML);
            setIsEmpty(!hasContent);
        }
    };

    // 处理图片双击事件
    const handleImageDoubleClick = (e) => {
        if (e.target.tagName === 'IMG' && e.target.src) {
            setPreviewImage(e.target.src);
            setPreviewVisible(true);
        }
    };

    // 关闭预览
    const handleClosePreview = () => {
        setPreviewVisible(false);
        setPreviewImage('');
    };

    // 处理附件上传
    const handleAttachmentUpload = async (file) => {
        console.log('Attachment upload parameters:', { workorderId, equipmentId, facilityId });
        
        if (!workorderId || !equipmentId || !facilityId) {
            console.error('Missing required parameters:', { workorderId, equipmentId, facilityId });
            message.error(`Missing required parameters. Cannot upload attachment. workorderId: ${workorderId}, equipmentId: ${equipmentId}, facilityId: ${facilityId}`);
            return false;
        }

        setUploading(true);
        
        // 构造与Upload组件兼容的文件对象
        const fileObj = {
            originFileObj: file,
            name: file.name,
            size: file.size,
            type: file.type
        };
        
        const uploadData = {
            table_name: 'workorder',
            record_id: workorderId,
            facility: facilityId,
            files: [fileObj], // api期望files是数组，且每个文件有originFileObj属性
            equipment: equipmentId
        };
        
        console.log('Upload data:', uploadData);
        
        try {
            const response = await api.uploadFile(uploadData);
            
            if (response.status >= 200 && response.status < 300 && response.data && response.data.length > 0) {
                const uploadedFile = response.data[0];
                const attachmentContent = [`${ATTACHMENT_PREFIX}${uploadedFile.image}`];
                
                // 发送附件消息
                if (onSend) {
                    onSend(attachmentContent);
                }
                
                message.success('Attachment uploaded successfully');
            } else {
                message.error('Failed to upload attachment');
            }
        } catch (error) {
            console.error('Attachment upload error:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            message.error(`Failed to upload attachment: ${errorMsg}`);
        } finally {
            setUploading(false);
        }
        
        return false; // 阻止默认上传行为
    };

    // 处理粘贴事件，支持图片和文本粘贴
    const handlePaste = (e) => {
        e.preventDefault();
        
        const clipboardData = e.clipboardData || window.clipboardData;
        const items = clipboardData.items;
        
        // 检查是否有图片文件
        let hasImage = false;
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            
            // 处理图片文件
            if (item.type.indexOf('image') !== -1) {
                hasImage = true;
                const file = item.getAsFile();
                
                if (file) {
                    const reader = new FileReader();
                    reader.onload = function(event) {
                        const imgSrc = event.target.result;
                        const imgId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                        
                        // 创建一个包装div来容纳Image组件
                        const imgHtml = `<div class="message-image-wrapper" data-src="${imgSrc}" data-id="${imgId}">
                            <img src="${imgSrc}" alt="Pasted image" class="message-image" style="max-width: 100%; height: auto; margin: 5px 0; border-radius: 4px; cursor: pointer;" />
                        </div>`;
                        
                        // 插入图片到编辑器
                        document.execCommand('insertHTML', false, imgHtml);
                        updateContent();
                    };
                    reader.readAsDataURL(file);
                }
                break;
            }
        }
        
        // 如果没有图片，处理文本内容
        if (!hasImage) {
            let pastedData = clipboardData.getData('text/html');
            
            // 如果没有HTML内容，使用纯文本
            if (!pastedData) {
                pastedData = clipboardData.getData('text/plain');
                // 将纯文本转换为HTML格式，保持换行
                pastedData = pastedData.replace(/\n/g, '<br>');
                document.execCommand('insertHTML', false, pastedData);
            } else {
                // 处理HTML内容，确保图片能正确显示
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = pastedData;
                
                // 处理现有图片标签
                const images = tempDiv.querySelectorAll('img');
                images.forEach((img, index) => {
                    const imgSrc = img.src;
                    const imgId = `img_${Date.now()}_${index}`;
                    
                    // 创建包装div
                    const wrapper = document.createElement('div');
                    wrapper.className = 'message-image-wrapper';
                    wrapper.setAttribute('data-src', imgSrc);
                    wrapper.setAttribute('data-id', imgId);
                    
                    // 设置图片样式
                    img.className = 'message-image';
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    img.style.margin = '5px 0';
                    img.style.borderRadius = '4px';
                    img.style.cursor = 'pointer';
                    
                    // 用包装div替换原图片
                    img.parentNode.insertBefore(wrapper, img);
                    wrapper.appendChild(img);
                });
                
                // 插入处理后的内容
                document.execCommand('insertHTML', false, tempDiv.innerHTML);
            }
            
            // 延迟更新内容，确保内容已插入
            setTimeout(updateContent, 10);
        }
    };

    // 处理键盘事件
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        }
    };

    // 发送消息
    const handleSend = () => {
        if (!isEmpty && onSend && !disabled) {
            // 解析内容为数组格式
            const contentArray = parseContentToArray(content);
            onSend(contentArray);
            // 清空编辑器并显示占位符
            if (editorRef.current) {
                editorRef.current.innerHTML = `<p style="color: #999; margin: 0;">${placeholder}</p>`;
                setContent('');
                setIsEmpty(true);
            }
        }
    };

    // 初始化编辑器
    useEffect(() => {
        if (editorRef.current) {
            // 设置初始占位符
            if (isEmpty) {
                editorRef.current.innerHTML = `<p style="color: #999; margin: 0;">${placeholder}</p>`;
            }
        }
    }, [placeholder]);

    // 处理焦点事件
    const handleFocus = () => {
        if (editorRef.current && isEmpty) {
            // 检查是否真的为空（没有图片和文本）
            const textContent = editorRef.current.textContent || editorRef.current.innerText;
            const hasImages = editorRef.current.querySelectorAll('img').length > 0;
            
            if (!textContent.trim() && !hasImages) {
                editorRef.current.innerHTML = '';
            }
        }
    };

    const handleBlur = () => {
        if (editorRef.current) {
            const textContent = editorRef.current.textContent || editorRef.current.innerText;
            const hasImages = editorRef.current.querySelectorAll('img').length > 0;
            
            // 只有在真正为空时才显示占位符
            if (!textContent.trim() && !hasImages) {
                editorRef.current.innerHTML = `<p style="color: #999; margin: 0;">${placeholder}</p>`;
                setIsEmpty(true);
            }
        }
    };

    return (
        <div className="message-editor">
            <div className="message-input-wrapper">
                <div
                    ref={editorRef}
                    className="message-input-editor"
                    contentEditable={!disabled}
                    onInput={updateContent}
                    onPaste={handlePaste}
                    onKeyPress={handleKeyPress}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onDoubleClick={handleImageDoubleClick}
                    style={{
                        minHeight: '40px',
                        maxHeight: '120px',
                        overflowY: 'auto',
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        outline: 'none',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        backgroundColor: disabled ? '#f5f5f5' : '#fff',
                        cursor: disabled ? 'not-allowed' : 'text'
                    }}
                />
                <Upload
                    beforeUpload={handleAttachmentUpload}
                    showUploadList={false}
                    disabled={disabled || uploading}
                >
                    <Button
                        icon={<PaperClipOutlined />}
                        disabled={disabled || uploading}
                        loading={uploading}
                        style={{ marginLeft: '8px' }}
                        title="Upload attachment"
                    />
                </Upload>
                <Button
                    type="primary"
                    onClick={handleSend}
                    disabled={isEmpty || disabled}
                    className="send-button"
                    icon={<i className="fas fa-paper-plane" />}
                    style={{ marginLeft: '8px' }}
                />
            </div>
            
            {/* Modal 图片预览 */}
            <ImagePreviewModal
                visible={previewVisible}
                src={previewImage}
                onClose={handleClosePreview}
            />
        </div>
    );
};

export default MessageEditor;